// TanStack Query wrappers for /v1/datasets.
//
// Caching strategy:
// - list: 30s stale (matches global default in query-client.ts) — datasets
//   change rarely after upload, so background refetch on focus is overkill.
// - one: 60s stale — same reasoning.
// - upload + delete: invalidate the list on success so the table reflects
//   the change without manual `refetch()`.

import {
  type Dataset,
  type DatasetListResponse,
  type ListDatasetsParams,
  type UploadDatasetOptions,
  type UploadDatasetResult,
  deleteDataset,
  getDataset,
  listDatasets,
  uploadDataset,
} from '@/lib/datasets';
import {
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

export const DATASETS_QUERY_KEY = ['datasets'] as const;
export const DATASET_QUERY_KEY = (id: string) => ['dataset', id] as const;

export function useDatasetsList(params: ListDatasetsParams = {}) {
  return useQuery<DatasetListResponse, AxiosError>({
    queryKey: [...DATASETS_QUERY_KEY, params],
    queryFn: () => listDatasets(params),
    staleTime: 30_000,
  });
}

export function useDataset(id: string | undefined) {
  return useQuery<Dataset, AxiosError>({
    queryKey: id ? DATASET_QUERY_KEY(id) : ['dataset', 'noop'],
    queryFn: () => getDataset(id as string),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export interface UploadVariables {
  file: File;
  options?: UploadDatasetOptions;
}

export function useUploadDataset(
  options?: Omit<
    UseMutationOptions<UploadDatasetResult, AxiosError, UploadVariables>,
    'mutationFn'
  >,
) {
  const qc = useQueryClient();
  // Spread caller options FIRST so our wrapped onSuccess wins — otherwise the
  // call site (CsvUpload) would clobber our invalidateQueries entirely and the
  // table stays stale until the next manual refresh.
  return useMutation<UploadDatasetResult, AxiosError, UploadVariables>({
    mutationFn: ({ file, options: o }) => uploadDataset(file, o),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: DATASETS_QUERY_KEY });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteDataset() {
  const qc = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (id) => deleteDataset(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DATASETS_QUERY_KEY });
    },
  });
}
